import { memo } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowTrendingUpIcon,
  ChartBarIcon,
  CursorArrowRaysIcon,
  LinkIcon,
} from '@heroicons/react/24/outline'
import { formatChange } from '../../../../shared/lib/analytics-format'
import type { DashboardStats } from '../../../../shared/types/dashboard'
import { useTranslation } from 'react-i18next'

interface StatsCardsProps {
  stats?: DashboardStats
}

/**
 * 대시보드 요약 지표.
 *
 * 아이콘 색은 하나로 둔다. 지표마다 다른 색을 주면 색이 뜻을 가진 것처럼
 * 보이는데 실제로는 아무 의미가 없다.
 */
/** 렌더마다 새로 만들면 framer-motion 이 값이 바뀐 것으로 보고 다시 움직인다 */
const FADE_IN = { opacity: 0, y: 16 }
const FADE_TO = { opacity: 1, y: 0 }

function StatsCards({ stats }: StatsCardsProps) {
  const { t } = useTranslation()
  const cards = [
    {
      title: t('dashboard.totalLinks'),
      value: (stats?.totalUrls ?? 0).toLocaleString(),
      change: stats?.urlsChange ?? 0,
      icon: LinkIcon,
      comparison: t('dashboard.vsLastWeek'),
    },
    {
      title: t('dashboard.totalClicks'),
      value: (stats?.totalClicks ?? 0).toLocaleString(),
      change: stats?.clicksChange ?? 0,
      icon: CursorArrowRaysIcon,
      comparison: t('dashboard.vsLastWeek'),
    },
    {
      title: t('dashboard.todayClicks'),
      value: (stats?.todayClicks ?? 0).toLocaleString(),
      change: stats?.todayChange ?? 0,
      icon: ArrowTrendingUpIcon,
      // todayChange 는 어제 클릭과 비교한 값이다
      comparison: t('dashboard.vsYesterday'),
    },
    {
      // 링크 중 클릭이 한 번이라도 발생한 비율
      title: t('dashboard.clickRate'),
      value: `${stats?.averageClickRate ?? 0}%`,
      change: stats?.clickRateChange ?? 0,
      icon: ChartBarIcon,
      comparison: t('dashboard.vsLastWeek'),
    },
  ]

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {cards.map((card, index) => {
        const changeTone =
          card.change === 0
            ? 'text-gray-400 dark:text-gray-500'
            : card.change > 0
              ? 'text-green-600 dark:text-green-400'
              : 'text-rose-600 dark:text-rose-400'

        return (
          <motion.div
            key={card.title}
            initial={FADE_IN}
            animate={FADE_TO}
            transition={{ delay: index * 0.05 }}
            className="flex flex-col gap-1.5 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="truncate text-[13px] font-medium text-gray-500 dark:text-gray-400">
                {card.title}
              </span>
              <card.icon className="h-[18px] w-[18px] shrink-0 text-primary-600 dark:text-primary-400" />
            </div>

            <span className="text-[30px] font-extrabold tabular-nums leading-tight tracking-tight text-gray-900 dark:text-white">
              {card.value}
            </span>

            <span className="text-[13px] text-gray-400 dark:text-gray-500">
              <span className={`font-semibold tabular-nums ${changeTone}`}>
                {formatChange(card.change)}
              </span>
              <span className="mx-1">·</span>
              {card.comparison}
            </span>
          </motion.div>
        )
      })}
    </div>
  )
}

/**
 * 모달을 열고 닫을 때마다 대시보드가 다시 그려진다. 지표는 그와 무관하므로
 * 값이 같으면 다시 그리지 않는다. 다시 그리면 카드가 한 번 깜빡인다.
 */
export default memo(StatsCards)
